import { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { useAuth } from "./hooks/useAuth";
import { useProfileStore } from "./stores/profileStore";
import { useOnboardingStore } from "./stores/onboardingStore";
import { useTokenRefreshTimer } from "./hooks/useTokenRefreshTimer";
import { AppLayout } from "./components/layout/AppLayout";
import { ProtectedRoute } from "./components/auth/ProtectedRoute";
import { LoginPage } from "./pages/LoginPage";
import { RegisterPage } from "./pages/RegisterPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { DashboardPage } from "./pages/DashboardPage";
import { SettingsProfilePage } from "./pages/SettingsProfilePage";
import { SettingsIntegrationsPage } from "./pages/SettingsIntegrationsPage";
import { ActivitiesPage } from "./pages/ActivitiesPage";
import { ActivityDetailPage } from "./pages/ActivityDetailPage";
import { ChatPage } from "./pages/ChatPage";
import { TrainingPlannerPage } from "./pages/TrainingPlannerPage";
import { LandingPage } from "./pages/LandingPage";
import { PrivacyPolicyPage } from "./pages/PrivacyPolicyPage";
import { TermsOfServicePage } from "./pages/TermsOfServicePage";

function ErrorFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="text-center p-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Something went wrong</h1>
        <p className="text-gray-600 mb-4">We've been notified and are working on a fix.</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Reload page
        </button>
      </div>
    </div>
  );
}

function AppRoutes() {
  const { checkSession, isAuthenticated, token } = useAuth();
  const { fetchProfile, clearProfile } = useProfileStore();
  const { checkStatus: checkOnboardingStatus } = useOnboardingStore();

  // Set up proactive token refresh timer
  useTokenRefreshTimer();

  // Check session on app load
  useEffect(() => {
    checkSession();
  }, [checkSession]);

  // Fetch profile and check onboarding when authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchProfile(token);
      checkOnboardingStatus();
    } else {
      clearProfile();
    }
  }, [isAuthenticated, token, fetchProfile, clearProfile, checkOnboardingStatus]);

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms-of-service" element={<TermsOfServicePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Protected routes with layout under /app */}
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="activities" element={<ActivitiesPage />} />
        <Route path="activities/:id" element={<ActivityDetailPage />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="settings/profile" element={<SettingsProfilePage />} />
        <Route path="settings/integrations" element={<SettingsIntegrationsPage />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="training-planner" element={<TrainingPlannerPage />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  );
}
